// Reads MSAL's own browser cache to decide whether a silent token acquisition can possibly
// succeed. MSAL exposes accounts but not credentials, and an account without a refresh token is
// exactly the case that costs the user ten seconds: acquireTokenSilent then falls back to a
// hidden iframe against login.microsoftonline.com, which never redirects back (no AAD session in
// that context, or a blocked third-party cookie) and times out on `iframeHashTimeout`. Measured
// 24/25 Aug 2026: 65 of 83 standalone logins that hit `monitor_window_timeout` had no refresh
// token cached at all. See HAM-4319.

/**
 * Entries MSAL writes per credential. Only the fields we match on; MSAL stores more.
 * `credentialType` has been stable across MSAL major versions, the cache *key* format has not,
 * which is why this matches on the parsed value instead of on the key.
 */
interface MsalCredentialEntry {
    credentialType?: string
    homeAccountId?: string
    clientId?: string
}

const REFRESH_TOKEN_CREDENTIAL_TYPE = 'RefreshToken'

/** The slice of the Storage API this needs; injectable so it can be exercised without a DOM. */
export type ReadableStorage = Pick<Storage, 'length' | 'key' | 'getItem'>

/**
 * Whether MSAL holds a refresh token for this account and client.
 *
 * **Fails open.** If the storage is unreadable, or nothing in it parses as an MSAL credential,
 * this returns `true` and the caller attempts the silent acquisition exactly as before. An MSAL
 * upgrade that changes the cache shape can therefore never block a login; the worst it can do is
 * bring back the ten seconds this is meant to save.
 */
export function hasRefreshToken(clientId: string, homeAccountId: string, storage: ReadableStorage = window.localStorage): boolean {
    if (!clientId || !homeAccountId) {
        return true
    }

    let recognizedAnyCredential = false
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i)
            if (!key) {
                continue
            }
            const entry = parseCredential(storage.getItem(key))
            if (!entry) {
                continue
            }
            recognizedAnyCredential = true
            if (
                entry.credentialType === REFRESH_TOKEN_CREDENTIAL_TYPE &&
                entry.homeAccountId === homeAccountId &&
                entry.clientId === clientId
            ) {
                return true
            }
        }
    } catch (e) {
        console.error(e)
        return true
    }

    return !recognizedAnyCredential
}

/** A stored value that looks like an MSAL credential, or undefined for everything else. */
function parseCredential(value: string | null): MsalCredentialEntry | undefined {
    // Cheap reject before parsing: the app stores plenty of unrelated JSON blobs, and every MSAL
    // credential carries this field.
    if (!value || !value.includes('"credentialType"')) {
        return undefined
    }
    try {
        const parsed = JSON.parse(value) as MsalCredentialEntry
        return typeof parsed?.credentialType === 'string' ? parsed : undefined
    } catch {
        return undefined
    }
}
